// Practice history in localStorage. Pure functions, no Vuex; rows carry ids so they sync 1:1.

const STORAGE_KEY = 'ezshadowHistory.v1';
const SYNC_STATE_KEY = 'ezshadowSync.v1';
// ~1 MB of localStorage. Unsynced rows are never dropped by the cap (mergeRemote).
const MAX_SESSIONS = 3000;
// Display cap only; grouping still spans every stored session.
const MAX_SENTENCE_GROUPS = 12;

let pendingHistory = null;
let flushTimer = null;
const FLUSH_DELAY_MS = 10000;
const DAY_TOTALS_REPAIR = 1;
let syncHook = null;

export function loadHistory() {
  if (pendingHistory) return pendingHistory;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.version === 1 && parsed.days && Array.isArray(parsed.sessions)) {
        return parsed;
      }
    }
  } catch (e) { /* corrupt -> start fresh */ }
  return emptyHistory();
}

function emptyHistory() {
  return {
    version: 1,
    totalPracticeSeconds: 0,
    dayTotalsRepair: DAY_TOTALS_REPAIR, // born clean; see repairSyncedDayTotalsOnce
    days: {}, // "YYYY-MM-DD" (local time) -> { practiceSeconds, sessions }
    sessions: [] // newest first, capped at MAX_SESSIONS
  };
}

// Position key "video:lang:half-seconds": there is no stable sentence id, and an
// integer avoids float-string keys. Known limit: refinement moves boundaries,
// so one sentence can get two keys.
export function sentenceKey(videoId, lang, segStart) {
  if (!videoId || !lang) return null;
  if (typeof segStart !== 'number' || !isFinite(segStart)) return null;
  return `${videoId}:${lang}:${Math.round(segStart * 2)}`;
}

/** @param {{videoId, segStart, segEnd, lang, accuracy, refWordCount, practicedAt, durationSec}} session */
export function addSession(session) {
  const history = loadHistory();
  const day = localDateKey(new Date(session.practicedAt || Date.now()));

  const row = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    practicedAt: new Date().toISOString(),
    ...session
  };
  // Derived after the spread so callers can't poison the per-sentence index.
  row.sentenceKey = sentenceKey(row.videoId, row.lang, row.segStart);
  // Without it the Worker files the row under the UTC date (BUGFIX-008).
  row.localDay = day;
  row.syncedAt = null;

  // New fields stay in version 1: older builds read the doc fine and old rows lack them.
  history.sessions.unshift(row);
  capKeepingUnsynced(history.sessions);

  const dayEntry = history.days[day] || { practiceSeconds: 0, sessions: 0 };
  dayEntry.sessions += 1;
  history.days[day] = dayEntry;

  scheduleFlush(history);
  return history;
}

export function addPracticeTime(seconds, date = new Date()) {
  if (!(seconds > 0)) return loadHistory();
  const history = loadHistory();
  const day = localDateKey(date);

  history.totalPracticeSeconds += seconds;
  const dayEntry = history.days[day] || { practiceSeconds: 0, sessions: 0 };
  dayEntry.practiceSeconds += seconds;
  history.days[day] = dayEntry;

  scheduleFlush(history);
  return history;
}

export function getStats(now = new Date()) {
  const history = loadHistory();
  const todayKey = localDateKey(now);
  const today = history.days[todayKey];

  // Newest first by time, not by array position: rows from other devices and
  // skewed clocks arrive out of order (BUGFIX-008).
  const sessions = history.sessions.slice().sort(byNewest);
  const scored = sessions.filter(s => typeof s.accuracy === 'number');
  const sevenDaysAgo = now.getTime() - 7 * 24 * 3600 * 1000;
  const scored7d = scored.filter(s => new Date(s.practicedAt).getTime() >= sevenDaysAgo);

  const perVideo = {};
  sessions.forEach(s => {
    const entry = perVideo[s.videoId] || { videoId: s.videoId, sessions: 0, lastPracticedAt: s.practicedAt, lastSegStart: s.segStart };
    entry.sessions += 1;
    if (s.practicedAt > entry.lastPracticedAt) {
      entry.lastPracticedAt = s.practicedAt;
      entry.lastSegStart = s.segStart;
    }
    perVideo[s.videoId] = entry;
  });

  return {
    totalSeconds: Math.round(history.totalPracticeSeconds),
    todaySeconds: Math.round((today && today.practiceSeconds) || 0),
    streakDays: computeStreak(history.days, now),
    // From day totals, not rows: the local log is capped at MAX_SESSIONS.
    sessionCount: Object.keys(history.days).reduce(function(sum, day) {
      return sum + (history.days[day].sessions || 0);
    }, 0),
    avgAccuracy7d: scored7d.length
      ? Math.round(scored7d.reduce((sum, s) => sum + s.accuracy, 0) / scored7d.length)
      : null,
    recentSessions: sessions.slice(0, 14),
    perVideo: Object.keys(perVideo)
      .map(id => perVideo[id])
      .sort((a, b) => (a.lastPracticedAt < b.lastPracticedAt ? 1 : -1)),
    perSentence: groupBySentence(sessions),
    byVideo: groupAllByVideo(sessions)
  };
}

function byNewest(a, b) {
  return a.practicedAt < b.practicedAt ? 1 : a.practicedAt > b.practicedAt ? -1 : 0;
}

function byOldest(a, b) {
  return byNewest(b, a);
}

// Only sentences with 2+ scored attempts: one try says nothing about progress.
export function groupBySentence(sessions) {
  const groups = {};
  const order = [];

  sessions.forEach(s => {
    // Old rows lack a key; skipped attempts lack accuracy.
    if (!s.sentenceKey || typeof s.accuracy !== 'number') return;

    let group = groups[s.sentenceKey];
    if (!group) {
      group = {
        sentenceKey: s.sentenceKey,
        videoId: s.videoId,
        lang: s.lang,
        segStart: s.segStart,
        segEnd: s.segEnd,
        lastPracticedAt: s.practicedAt,
        attempts: []
      };
      groups[s.sentenceKey] = group;
      order.push(group);
    }
    group.attempts.push({ accuracy: s.accuracy, practicedAt: s.practicedAt });
  });

  return order
    .filter(g => g.attempts.length > 1)
    .slice(0, MAX_SENTENCE_GROUPS)
    .map(g => {
      // reverse() first so tied timestamps keep insertion order.
      g.attempts.reverse().sort(byOldest); // the view reads left to right in time
      const accuracies = g.attempts.map(a => a.accuracy);
      g.attemptCount = accuracies.length;
      g.bestAccuracy = Math.max.apply(null, accuracies);
      g.firstAccuracy = accuracies[0];
      g.lastAccuracy = accuracies[accuracies.length - 1];
      g.delta = g.lastAccuracy - g.firstAccuracy;
      return g;
    });
}

// Videos by recency, sentences by segStart, attempts oldest first; no attempt-count filter.
export function groupAllByVideo(sessions) {
  var videoMap = {};
  var videoOrder = [];

  sessions.forEach(function(s) {
    if (!s.sentenceKey || typeof s.accuracy !== 'number') return;

    if (!videoMap[s.videoId]) {
      videoMap[s.videoId] = { videoId: s.videoId, lastPracticedAt: s.practicedAt, sentenceMap: {}, sentenceOrder: [] };
      videoOrder.push(s.videoId);
    }
    var video = videoMap[s.videoId];
    if (s.practicedAt > video.lastPracticedAt) video.lastPracticedAt = s.practicedAt;

    if (!video.sentenceMap[s.sentenceKey]) {
      video.sentenceMap[s.sentenceKey] = {
        sentenceKey: s.sentenceKey,
        segStart: s.segStart,
        segEnd: s.segEnd,
        lastPracticedAt: s.practicedAt,
        attempts: []
      };
      video.sentenceOrder.push(s.sentenceKey);
    }
    var sentence = video.sentenceMap[s.sentenceKey];
    if (s.practicedAt > sentence.lastPracticedAt) sentence.lastPracticedAt = s.practicedAt;
    sentence.attempts.push({ accuracy: s.accuracy, practicedAt: s.practicedAt, durationSec: s.durationSec });
  });

  return videoOrder
    .map(function(id) {
      var v = videoMap[id];
      var sentences = v.sentenceOrder
        .map(function(sk) {
          var s = v.sentenceMap[sk];
          s.attempts.reverse().sort(byOldest); // left-to-right bar chart, ties in insertion order
          s.attemptCount = s.attempts.length;
          s.lastAccuracy = s.attempts[s.attempts.length - 1].accuracy;
          s.firstAccuracy = s.attempts[0].accuracy;
          s.delta = s.lastAccuracy - s.firstAccuracy;
          return s;
        })
        .sort(function(a, b) { return a.segStart - b.segStart; });
      return { videoId: v.videoId, lastPracticedAt: v.lastPracticedAt, sentences: sentences };
    })
    .sort(function(a, b) { return a.lastPracticedAt < b.lastPracticedAt ? 1 : -1; });
}

/** Consecutive practice days ending today, or yesterday if today has none yet. */
export function computeStreak(days, now = new Date()) {
  const cursor = new Date(now.getTime());
  if (!hasPractice(days, localDateKey(cursor))) {
    cursor.setDate(cursor.getDate() - 1); // allow the streak to end yesterday
    if (!hasPractice(days, localDateKey(cursor))) return 0;
  }

  let streak = 0;
  while (hasPractice(days, localDateKey(cursor))) {
    streak += 1;
    cursor.setDate(cursor.getDate() - 1);
  }
  return streak;
}

function hasPractice(days, key) {
  const entry = days[key];
  return !!entry && (entry.practiceSeconds > 0 || entry.sessions > 0);
}

export function flushHistory() {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (!pendingHistory) return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pendingHistory));
    pendingHistory = null;
    if (syncHook) {
      try { syncHook(); } catch (_) {}
    }
  } catch (e) {
    pendingHistory.sessions.length = Math.floor(pendingHistory.sessions.length / 2);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(pendingHistory));
      pendingHistory = null;
      if (syncHook) {
        try { syncHook(); } catch (_) {}
      }
    } catch (e2) { /* storage unavailable; keep in memory */ }
  }
}

function scheduleFlush(history) {
  pendingHistory = history;
  if (!flushTimer) {
    flushTimer = setTimeout(flushHistory, FLUSH_DELAY_MS);
  }
}

export function localDateKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Called after every successful localStorage write (registered in main.js). */
export function setSyncHook(fn) {
  syncHook = fn;
}

export function getSyncState() {
  try {
    var raw = localStorage.getItem(SYNC_STATE_KEY);
    return raw ? JSON.parse(raw) : { userId: null, lastPulledAt: null };
  } catch (_) { return { userId: null, lastPulledAt: null }; }
}

export function setSyncState(next) {
  try { localStorage.setItem(SYNC_STATE_KEY, JSON.stringify(next)); } catch (_) {}
}

export function getUnsynced() {
  var history = loadHistory();
  return history.sessions
    .filter(isUnsynced)
    .map(function(s) {
      // Rows from before localDay was stored.
      return s.localDay ? s : Object.assign({}, s, { localDay: localDateKey(new Date(s.practicedAt)) });
    });
}

function isUnsynced(s) {
  return s.syncedAt === null || s.syncedAt === undefined;
}

/** Per-day practice seconds not yet pushed (tracked via syncedPracticeSeconds). */
export function getUnsyncedDayDeltas() {
  var history = loadHistory();
  var deltas = [];
  Object.keys(history.days).forEach(function(day) {
    var entry = history.days[day];
    var total = entry.practiceSeconds || 0;
    var pushed = entry.syncedPracticeSeconds || 0;
    if (total > pushed) {
      deltas.push({ localDay: day, practiceSeconds: total - pushed });
    }
  });
  return deltas;
}

export function markSynced(ids, at, daysPushed) {
  var history = loadHistory();
  var idSet = {};
  ids.forEach(function(id) { idSet[id] = true; });
  history.sessions.forEach(function(s) {
    if (idSet[s.id]) s.syncedAt = at;
  });
  if (daysPushed) {
    daysPushed.forEach(function(d) {
      var entry = history.days[d.localDay];
      if (entry) entry.syncedPracticeSeconds = (entry.syncedPracticeSeconds || 0) + d.practiceSeconds;
    });
  }
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(history)); } catch (_) {}
}

// Call BEFORE mergeRemote: skipping known ids leaves only rows from other devices.
// practiced_at compares as a string (ISO-8601 UTC).
export function remoteResumePoint(remoteSessions) {
  if (!remoteSessions || !remoteSessions.length) return null;
  var known = {};
  loadHistory().sessions.forEach(function(s) { known[s.id] = true; });

  var best = null;
  remoteSessions.forEach(function(row) {
    if (!row || known[row.id]) return;
    if (!row.video_id || typeof row.seg_start !== 'number' || !row.practiced_at) return;
    if (best && row.practiced_at <= best.practiced_at) return;
    best = row;
  });
  if (!best) return null;

  return {
    videoId: best.video_id,
    startTime: best.seg_start,
    lang: best.lang,
    at: best.practiced_at
  };
}

// Remote day seconds are absolute totals, so take the max per day.
export function mergeRemote(remoteSessions, remoteDayTotals) {
  var history = loadHistory();
  var localIds = {};
  history.sessions.forEach(function(s) { localIds[s.id] = true; });

  var added = 0;
  (remoteSessions || []).forEach(function(row) {
    if (localIds[row.id]) return;
    var s = {
      id: row.id,
      practicedAt: row.practiced_at,
      localDay: row.local_day,
      videoId: row.video_id,
      lang: row.lang,
      sentenceKey: row.sentence_key,
      segStart: row.seg_start,
      segEnd: row.seg_end,
      accuracy: row.accuracy,
      refWordCount: row.ref_word_count,
      durationSec: row.duration_sec,
      syncedAt: row.created_at
    };
    history.sessions.push(s);
    var day = row.local_day;
    var entry = history.days[day] || { practiceSeconds: 0, sessions: 0 };
    entry.sessions = (entry.sessions || 0) + 1;
    history.days[day] = entry;
    added++;
  });

  if (added > 0) {
    history.sessions.sort(byNewest);
    capKeepingUnsynced(history.sessions);
  }

  var unsyncedPerDay = {};
  history.sessions.forEach(function(s) {
    if (!isUnsynced(s)) return;
    var day = s.localDay || localDateKey(new Date(s.practicedAt));
    unsyncedPerDay[day] = (unsyncedPerDay[day] || 0) + 1;
  });

  // The server total R already holds everything this device pushed (P), so the
  // day is R plus what is still unpushed here (L - P), and R counts as pushed.
  // Treating R as unpushed re-sent other devices' time on every pull (BUGFIX-008).
  (remoteDayTotals || []).forEach(function(dt) {
    var day = dt.local_day;
    var entry = history.days[day] || { practiceSeconds: 0, sessions: 0 };
    var remoteTotal = Number(dt.practice_seconds) || 0;
    var unpushed = Math.max(0, (entry.practiceSeconds || 0) - (entry.syncedPracticeSeconds || 0));
    entry.practiceSeconds = remoteTotal + unpushed;
    entry.syncedPracticeSeconds = remoteTotal;
    // Same rule for the attempt count; rows past the local cap still count.
    entry.sessions = (Number(dt.session_count) || 0) + (unsyncedPerDay[day] || 0);
    history.days[day] = entry;
  });

  history.totalPracticeSeconds = Object.keys(history.days).reduce(function(sum, day) {
    return sum + ((history.days[day].practiceSeconds) || 0);
  }, 0);

  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(history)); } catch (_) {}
  return history;
}

// Drops the oldest synced rows past MAX_SESSIONS; unsynced rows exist nowhere else.
function capKeepingUnsynced(sessions) {
  for (var i = sessions.length - 1; i >= 0 && sessions.length > MAX_SESSIONS; i--) {
    if (!isUnsynced(sessions[i])) sessions.splice(i, 1);
  }
}

// One-time, signed-in devices only (the account's server totals are
// authoritative there). Histories synced by the pre-BUGFIX-008 client hold
// inflated day totals, and the gap between those and syncedPracticeSeconds
// looks exactly like unpushed practice, so mergeRemote kept it and push sent it
// back. Dropping the local day seconds lets the next pull take the server's.
// Loses at most a few seconds of genuinely unpushed time. Also re-pulls from
// the start to backfill attempts the old 500-row cap dropped.
export function repairSyncedDayTotalsOnce() {
  var history = loadHistory();
  if (history.dayTotalsRepair === DAY_TOTALS_REPAIR) return false;
  // Never synced here: its day totals are this device's own, nothing to repair.
  if (!getSyncState().userId) return false;
  Object.keys(history.days).forEach(function(day) {
    history.days[day].practiceSeconds = 0;
    history.days[day].syncedPracticeSeconds = 0;
  });
  history.totalPracticeSeconds = 0;
  history.dayTotalsRepair = DAY_TOTALS_REPAIR;
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(history)); } catch (_) {}
  // Epoch, not null: null means "first sign-in" and lets a resume point
  // override a newer local position (remoteResumePoint's fullPull).
  var state = getSyncState();
  setSyncState({ userId: state.userId, lastPulledAt: '1970-01-01T00:00:00.000Z' });
  return true;
}

/** Archive under a user-scoped key and reset, so a new account doesn't inherit the old user's data. */
export function archiveHistoryForUser(oldUserId) {
  try {
    var raw = localStorage.getItem(STORAGE_KEY);
    if (raw) localStorage.setItem(STORAGE_KEY + '.' + oldUserId, raw);
    localStorage.removeItem(STORAGE_KEY);
    pendingHistory = null;
  } catch (_) {}
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) flushHistory();
  });
}
